import { FC, useEffect } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFeedTags, setAnswer } from '@/store/reducers/survey/surveySlice';
import { SHOPPING_FEED } from '@/constants/feed';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import SurveyDefaultStep from '@/components/SurveyDefaultStep';
import analytics from '@/utils/analytics/analyticsInstance';

const Main = styled(Box)(() => ({
  gap: 24,
  padding: 16,
  paddingBottom: 96,
  display: 'flex',
  flexDirection: 'column',
}));

const Image = styled('img')(() => ({
  width: 32,
  height: 32,
}));

const TagsContainer = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
}));

const TagItem = styled('button')<{ active: boolean }>(({ theme, active }) => ({
  fontSize: 16,
  fontWeight: 600,
  paddingInline: 12,
  paddingBlock: 8,
  borderWidth: 1,
  borderRadius: 8,
  cursor: 'pointer',
  borderStyle: 'solid',
  transition: 'all 0.2s ease',
  color: active ? theme.palette.primary.contrastText : 'black',
  backgroundColor: active ? theme.palette.border.contrastText : 'transparent',
  borderColor: theme.palette.border.main,

  '@media (any-hover: hover)': {
    '&:hover': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.border.contrastText,
    },
  },
}));

const CustomizeFeedPage: FC = () => {
  const { next } = useSurveyNavigate();
  const dispatch = useAppDispatch();
  const selectedTags = useAppSelector(selectFeedTags);
  const question = 'Customize your shopping feed';

  const handleSubmit = () => {
    analytics.track('webOnboardingQuestionAnswered', {
      question,
      answer: selectedTags,
    });
    next();
  };

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: question,
    });
  }, []);

  return (
    <SurveyDefaultStep
      title={{ value: question, lineHeight: '32px', fontSize: 28 }}
      subTitle={{ value: 'Choose what you love — we’ll do the rest.' }}
      button={{
        onClick: handleSubmit,
        disabled: !selectedTags.length,
      }}
      footer={{
        position: 'fixed',
        sx: {
          bottom: -1,
          left: -1,
          right: -1,
          padding: 0,
          height: 81,
          paddingInline: '17px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
        },
      }}
    >
      <Main>
        {Object.values(SHOPPING_FEED).map((item, index) => (
          <Box key={index} gap={1} display="flex" flexDirection="column">
            <Box gap={1.5} display="flex" alignItems="center">
              <Image src={item.image} />
              <Typography fontSize={20} fontWeight={700} lineHeight="24px">
                {item.title}
              </Typography>
            </Box>
            <TagsContainer>
              {item.tags.map((item, index) => (
                <TagItem
                  key={index}
                  active={selectedTags.includes(item)}
                  onClick={() => dispatch(setAnswer({ key: 'feedTags', value: item }))}
                >
                  {item}
                </TagItem>
              ))}
            </TagsContainer>
          </Box>
        ))}
      </Main>
    </SurveyDefaultStep>
  );
};

export default CustomizeFeedPage;
